import { CardHeader, createMultiStyleConfigHelpers, Box, ResponsiveValue } from '@chakra-ui/react';
import { cardAnatomy } from '@chakra-ui/anatomy';

import Video, { VideoProps } from '@/components/atomic/v1/video/Video';

import { CardBody } from './CardBody';
import { CommonCardProps } from './cardTheme';
import { CardContainer } from './CardContainer';
import { CardImage } from './CardImage';

const { definePartsStyle } = createMultiStyleConfigHelpers(cardAnatomy.keys);

export const myomersMediaVariant = definePartsStyle({
  header: {
    flex: 1
  },
  body: {
    flex: 1
  }
});

type ImageOrVideoProps =
  | {
      imageSource: string;
      imageAlt: string;
      videoSource?: never;
    }
  | {
      imageSource?: never;
      imageAlt?: never;
      videoSource: VideoProps;
    };

type FilteredCardProps = Omit<
  CommonCardProps,
  'icon' | 'iconSize' | 'colorStrip' | 'colorStripPosition' | 'colorStripColor'
>;

type MediaCardProps = FilteredCardProps &
  ImageOrVideoProps & {
    reverseHorizontalLayout?: boolean;
    mediaRightMargin?: number;
    mobileHorizontalLayout?: boolean;
    imageWidth?: ResponsiveValue<string>;
    cardBorderRadius?: ResponsiveValue<number | string>;
    isExternalLink?: boolean;
  };

/**
 *
 * MediaCard myOMERS component
 *
 * This `MediaCard` should only be used when an image/video needs to be added as part of the card component.
 *
 * This component has support for two layouts: vertical (the default) and horizontal. When set to horizontal, the layout only changes above the medium breakpoint set in the theme.
 *
 * When the layout is horizontal, and the viewport width above the medium breakpoint, the image/video is placed at the left and the rest of the card is on the right by default. This direction can be reversed, however, using the `reverseHorizontalLayout` prop.
 *
 * Also note that this component does not support a color strip.
 *
 */
export const MediaCard = (props: MediaCardProps) => {
  const {
    bodyContent,
    headerText,
    eyebrowText,
    footerLinkText,
    footerLinkUrl,
    primaryBtnText,
    primaryBtnUrl,
    secondaryBtnText,
    secondaryBtnUrl,
    secondaryBtnRightIconName,
    secondaryBtnSize,
    imageSource = null,
    videoSource,
    imageAlt,
    layout = 'vertical',
    reverseHorizontalLayout = false,
    cardOutline = 'drop-shadow',
    btnColor = 'blue',
    mediaRightMargin = 0,
    mobileHorizontalLayout = false,
    imageWidth = '100%',
    cardBorderRadius = '10px',
    isExternalLink = false
  } = props;

  const layoutIsHorizontal = layout === 'horizontal';

  return (
    <CardContainer
      variant="myomersMedia"
      layout={layout}
      cardOutline={cardOutline}
      reverseHorizontalLayout={reverseHorizontalLayout}
      pr={{ md: layoutIsHorizontal && !reverseHorizontalLayout ? 8 : 0 }}
      pl={{ md: layoutIsHorizontal && reverseHorizontalLayout ? 8 : 0 }}
      mobileHorizontalLayout={mobileHorizontalLayout}
      borderRadius={cardBorderRadius}
    >
      <CardHeader position="relative" maxW={imageWidth}>
        <Box
          height={{
            base: mobileHorizontalLayout ? '100%' : '270px',
            md: layout === 'vertical' ? '270px' : '100%'
          }}
          mr={{ base: 0, md: mediaRightMargin }}
          width={imageWidth}
        >
          {imageSource && <CardImage imageSource={imageSource} alt={imageAlt as string} />}
          {videoSource && <Video {...videoSource} />}
        </Box>
      </CardHeader>
      <CardBody
        bodyContent={bodyContent}
        headerText={headerText}
        eyebrowText={eyebrowText}
        footerLinkText={footerLinkText}
        footerLinkUrl={footerLinkUrl}
        primaryBtnText={primaryBtnText}
        primaryBtnUrl={primaryBtnUrl}
        secondaryBtnText={secondaryBtnText}
        secondaryBtnUrl={secondaryBtnUrl}
        secondaryBtnRightIconName={secondaryBtnRightIconName}
        secondaryBtnSize={secondaryBtnSize}
        isExternalSecondaryBtn={isExternalLink}
        btnColor={btnColor}
        pt={{ base: mobileHorizontalLayout ? 8 : 0, md: layoutIsHorizontal ? 8 : 0 }}
        pb={{ base: mobileHorizontalLayout ? 8 : 4, md: 8 }}
        px={{ base: 4, md: layoutIsHorizontal ? 0 : 4 }}
      />
    </CardContainer>
  );
};
